
import { useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import OrderAnalyticsDashboard from "@/components/admin/analytics/OrderAnalyticsDashboard";
import OrderReportGenerator from "@/components/admin/orders/OrderReportGenerator";
import OrderTrendsChart from "@/components/admin/orders/OrderTrendsChart";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BarChart3, FileText, TrendingUp, Calendar } from "lucide-react";

const AdminOrderAnalytics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'quarter'>('month');

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-admin-foreground">Order Analytics & Reporting</h1>
          <p className="text-admin-muted-foreground mt-1">
            Comprehensive analytics and reporting for order management
          </p>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview" className="flex items-center space-x-2">
              <BarChart3 className="h-4 w-4" />
              <span>Overview</span>
            </TabsTrigger>
            <TabsTrigger value="trends" className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4" />
              <span>Trends</span>
            </TabsTrigger>
            <TabsTrigger value="reports" className="flex items-center space-x-2">
              <FileText className="h-4 w-4" />
              <span>Reports</span>
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center space-x-2">
              <Calendar className="h-4 w-4" />
              <span>Calendar View</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <OrderAnalyticsDashboard />
          </TabsContent>

          <TabsContent value="trends" className="space-y-6">
            <Card className="admin-card border-admin-border">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-admin-foreground">Time Period</CardTitle>
                <div className="flex space-x-2">
                  <Button
                    variant={selectedPeriod === 'week' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedPeriod('week')}
                  >
                    Week
                  </Button>
                  <Button
                    variant={selectedPeriod === 'month' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedPeriod('month')}
                  >
                    Month
                  </Button>
                  <Button
                    variant={selectedPeriod === 'quarter' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedPeriod('quarter')}
                  >
                    Quarter
                  </Button>
                </div>
              </CardHeader>
            </Card>
            <OrderTrendsChart period={selectedPeriod} />
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <OrderReportGenerator />
          </TabsContent>

          <TabsContent value="calendar" className="space-y-6">
            <Card className="admin-card border-admin-border">
              <CardHeader>
                <CardTitle className="text-admin-foreground">Calendar View</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12">
                  <Calendar className="h-16 w-16 text-admin-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-admin-foreground mb-2">Calendar View Coming Soon</h3>
                  <p className="text-admin-muted-foreground">
                    Interactive calendar view for order analytics will be available in the next update.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminOrderAnalytics;
